import React from "react";
import styles from "./CoreList.module.css";
import { Typography } from "@mui/material";
import { CoreCheckbox } from "../checkbox/Checkbox";
import CoreChip, { CoreChipProps } from "../chip";
import ArrowUp from "../images/stepper-arrow.svg?react";
import i18nPackage from "../../i18n/i18n";
import { CoreIconButton } from "../button/Button";

type CoreListGroupProps = {
	title: string;
	children: React.ReactNode;
	isSelected: boolean;
	isIndeterminate?: boolean;
	onSelect: (selected: boolean) => void;
	expanded: boolean;
	onExpand: (expanded: boolean) => void;
	slotProps?: {
		chip?: {
			chipColor?: string;
			chipProps?: Omit<CoreChipProps, "children" | "chipColor">;
			text: React.ReactNode;
		};
	};
};

/**
 * Collapsable list for CoreList
 */
export function CoreListGroup(props: CoreListGroupProps) {
	const { children, isSelected, onSelect, title, slotProps, expanded, onExpand, isIndeterminate } = props;

	const handleExpand = () => {
		onExpand(!expanded);
	};

	const buttonAriaLabelKey = expanded ? "groupButtonAriaLabelCollapse" : "groupButtonAriaLabelExpand";
	const checkboxAriaLabelKey = isSelected ? "deselectAllAriaLabel" : "selectAllAriaLabel";

	return (
		<li className={styles.listGroupContainer}>
			<div className={styles.listGroupRow}>
				<div className={styles.listGroupTitleWrapper}>
					<CoreCheckbox
						checked={isSelected}
						onChange={(_, checked) => onSelect(checked)}
						inputProps={{ "aria-label": i18nPackage.t(`coreComponents.coreList.${checkboxAriaLabelKey}`, { group: title }) }}
						indeterminate={isIndeterminate}
					/>
					<CoreIconButton
						aria-expanded={expanded}
						onClick={handleExpand}
						variant="plain"
						aria-label={i18nPackage.t(`coreComponents.coreList.${buttonAriaLabelKey}`, { group: title })}
						style={{ padding: "4px" }}
					>
						<ArrowUp className={`${styles.arrow} ${expanded ? styles.expanded : ""}`} aria-hidden={true} />
					</CoreIconButton>
					<Typography>{title}</Typography>
				</div>
				{slotProps?.chip && (
					<CoreChip
						className={styles.countChip}
						{...slotProps?.chip?.chipProps}
						chipColor={slotProps?.chip?.chipColor ?? "#E8E8E8"}
					>
						{slotProps.chip.text}
					</CoreChip>
				)}
			</div>
			{expanded && <ul style={{ padding: 0 }}>{children}</ul>}
		</li>
	);
}

type CoreListRowProps = {
	// Probably should make the consumers pass in their own component
	checked?: boolean;
	onSelect?: (selected: boolean) => void;

	icon?: React.ReactNode;
	action?: React.ReactNode;
	label?: React.ReactNode;
	checkboxAriaLabel?: string;
	className?: string;
	disabled?: boolean;
};

export function CoreListRow({ checked, onSelect, label, checkboxAriaLabel, action, icon, className, disabled }: CoreListRowProps) {
	const labelText = checkboxAriaLabel ?? (typeof label === "string" ? label : undefined);
	const checkboxAriaLabelKey = checked ? "deselectOneAriaLabel" : "selectOneAriaLabel";
	const genericAriaLabelKey = checked ? "deselectOneGeneric" : "selectOneGeneric";
	return (
		<li className={`${styles.listRowContainer} ${className || ""}`}>
			<div className={styles.labelWrapper}>
				{checked !== undefined && onSelect && (
					<CoreCheckbox
						checked={checked}
						onChange={(_, checked) => onSelect(checked)}
						inputProps={{
							"aria-label": labelText
								? i18nPackage.t(`coreComponents.coreList.${checkboxAriaLabelKey}`, { label: labelText })
								: i18nPackage.t(`coreComponents.coreList.${genericAriaLabelKey}`)
						}}
						disabled={disabled}
					/>
				)}
				{icon}
				{typeof label === "string" ? <Typography className={styles.rowLabel}>{label}</Typography> : label}
			</div>
			{action}
		</li>
	);
}

type CoreListProps = {
	maxHeight?: number;
	maxWidth?: React.CSSProperties["maxWidth"];
	children: React.ReactNode;
	id?: string;
};

export default function CoreList({ maxHeight, children, id, maxWidth }: CoreListProps) {
	const finalHeight = `${maxHeight ?? 400}px`;
	return (
		<div className={styles.scrollWrapper} style={{ maxHeight: finalHeight, maxWidth: maxWidth ?? "" }} id={id}>
			<ul className={styles.listContainer} role="list">
				{children}
			</ul>
		</div>
	);
}
